/* eslint-disable consistent-return */
import { check } from 'meteor/check';

import { isEmpty } from 'lodash';
import * as telemetryConnectionOps from '../telemetry-connection-operations';
import TelemetryConnections from '../TelemetryConnections';
import checkAuthentication from '../../../modules/server/check-authentication';
import handleMethodException from '../../../modules/handle-method-exception';

export const editDocumentSchema = TelemetryConnections.schema.omit(
  'owner',
  'createdAt',
  'updatedAt',
  'selected',
  'deleted'
);
editDocumentSchema.extend({ _id: String });

/**
 * Inserts the telemetry connection in the database.
 *
 * @async
 * @param {object} telemetryConnectionObject - The telemetry connection object.
 * @param {string} userId - The user id.
 * @returns {string} Return the id of the new telemetry connection.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = (telemetryConnectionObject, userId) => {
  check(userId, String);
  try {
    checkAuthentication();
    TelemetryConnections.schema.validate(telemetryConnectionObject, {
      check,
      keys: ['name']
    });
    return TelemetryConnections.insert({ owner: userId, ...telemetryConnectionObject });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the telemetry connection in the database.
 *
 * @param {object} telemetryConnectionObject - The telemetry connection object.
 * @returns {string} Return the id of the updated telemetry connection.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = telemetryConnectionObject => {
  try {
    checkAuthentication();
    editDocumentSchema.validate(telemetryConnectionObject);
    const telemetryConnectionId = telemetryConnectionOps.getId(telemetryConnectionObject);
    if (isEmpty(telemetryConnectionObject.ip)) {
      TelemetryConnections.update(telemetryConnectionId, {
        $unset: { ip: '' },
        $set: telemetryConnectionObject
      });
    }
    if (isEmpty(telemetryConnectionObject.domain)) {
      TelemetryConnections.update(telemetryConnectionId, {
        $unset: { domain: '' },
        $set: telemetryConnectionObject
      });
    }

    return telemetryConnectionId;
    // Return _id so we can redirect to telemetry connection after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Copies the telemetry connection in the database.
 *
 * @param {string} telemetryConnectionId - The telemetry connection id.
 * @param {string} userId - The user id that wants the telemetry connection.
 * @returns {string} Return the id of the new telemetry connection copied.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const copy = (telemetryConnectionId, userId) => {
  check(telemetryConnectionId, String);
  check(userId, String);
  try {
    checkAuthentication();
    const copyDocumentData = TelemetryConnections.findOne({ _id: telemetryConnectionId });
    delete copyDocumentData.owner;
    delete copyDocumentData._id;
    delete copyDocumentData.createdAt;
    delete copyDocumentData.updatedAt;
    delete copyDocumentData.deleted;
    copyDocumentData.name += ' - copy';
    const copyDocumentId = TelemetryConnections.insert({
      owner: userId,
      ...copyDocumentData
    });
    return copyDocumentId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the telemetry connection in the database.
 *
 * @param {string} telemetryConnectionId - The telemetry connection id.
 * @returns {string} Return the id of the telemetry connection.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDelete = telemetryConnectionId => {
  check(telemetryConnectionId, String);
  try {
    checkAuthentication();
    return TelemetryConnections.update(telemetryConnectionId, {
      $set: { deleted: new Date().toISOString() }
    });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the telemetry connection in the database.
 *
 * @param {string} telemetryConnectionId - The telemetry connection id.
 * @returns {string} Return the id of the telemetry connection.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restore = telemetryConnectionId => {
  check(telemetryConnectionId, String);
  try {
    checkAuthentication();
    return TelemetryConnections.update(telemetryConnectionId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the telemetry connection from database.
 *
 * @param {string} telemetryConnectionId - The telemetry connection id.
 * @returns {string} Return the id of the deleted telemetry connection.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDelete = telemetryConnectionId => {
  check(telemetryConnectionId, String);
  try {
    checkAuthentication();
    return TelemetryConnections.remove(telemetryConnectionId);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the telemetry connection selected to true and the rest to false.
 *
 * @param {string} telemetryConnectionId - The telemetry connection id.
 * @param {boolean} isSelected - The new value for the selected key.
 * @param {string} userId - The user id.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const toggleSelected = (telemetryConnectionId, isSelected, userId) => {
  check(telemetryConnectionId, String);
  check(isSelected, Boolean);
  try {
    checkAuthentication();
    if (!isSelected) {
      TelemetryConnections.update(
        { owner: userId },
        { $set: { selected: false } },
        { multi: true }
      );
    }
    TelemetryConnections.update(telemetryConnectionId, { $set: { selected: !isSelected } });
  } catch (exception) {
    handleMethodException(exception);
  }
};
